import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  Boxes,
  ExternalLink,
  Package,
  Plus,
  ShoppingCart,
  Store,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation, useAuthSession, buildStoreUrl } from '@dukanti/shared';
import type { StoreOrderRow } from '@dukanti/shared';
import { useStockWorklist, type StockWorklistRow } from '@dukanti/shared/admin';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useCurrentStore } from '@/hooks/use-current-store';
import { useProducts } from '@/hooks/use-products';
import { useStoreOrders } from '@/hooks/use-store-orders';
import { useStore } from '@/features/store-settings/use-store';
import { StoreStatusBadge } from '@/features/publish-store/components/store-status-badge';
import { PublishStoreCard } from '@/features/publish-store/components/publish-store-card';
import { PublishedStoreCard } from '@/features/publish-store/components/published-store-card';
import { PublishStoreDialog } from '@/features/publish-store/components/publish-store-dialog';
import { UnpublishStoreDialog } from '@/features/publish-store/components/unpublish-store-dialog';

interface DashboardLocationState {
  justCreatedStore?: boolean;
  /** Rev 2: true when store was created but logo upload failed. */
  logoFailed?: boolean;
}

interface DashboardMetricCardProps {
  readonly testId: string;
  readonly title: string;
  readonly value: string;
  readonly helper: string;
  readonly icon: LucideIcon;
  readonly to?: string;
  readonly ariaLabel?: string;
  readonly isLoading?: boolean;
  readonly isError?: boolean;
  readonly loadingLabel: string;
  readonly errorLabel: string;
  readonly loadingHelper: string;
  readonly errorHelper: string;
}

function DashboardMetricCard({
  testId,
  title,
  value,
  helper,
  icon: Icon,
  to,
  ariaLabel,
  isLoading = false,
  isError = false,
  loadingLabel,
  errorLabel,
  loadingHelper,
  errorHelper,
}: DashboardMetricCardProps): React.JSX.Element {
  const displayValue = isLoading ? loadingLabel : isError ? errorLabel : value;
  const displayHelper = isLoading ? loadingHelper : isError ? errorHelper : helper;

  return (
    <section
      data-testid={testId}
      className="border-border bg-card shadow-admin-card min-h-36 rounded-lg border p-4"
    >
      <div className="flex items-start gap-3">
        <span className="bg-admin-lime text-admin-lime-foreground inline-flex size-10 shrink-0 items-center justify-center rounded-md">
          <Icon aria-hidden={true} className="size-5" />
        </span>
        <div className="min-w-0 flex-1 text-start">
          <h2 className="text-muted-foreground text-sm font-medium">{title}</h2>
          <p className="text-foreground mt-2 text-3xl font-semibold tracking-normal">
            {displayValue}
          </p>
          <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{displayHelper}</p>
        </div>
      </div>
      {to ? (
        <Link
          to={to}
          aria-label={ariaLabel}
          className="text-foreground hover:text-admin-lime-hover mt-4 inline-flex items-center gap-1 text-sm font-medium"
        >
          <ExternalLink aria-hidden={true} className="size-4 rtl:rotate-180" />
        </Link>
      ) : null}
    </section>
  );
}

function DashboardPanel({
  testId,
  title,
  actionLabel,
  actionTo,
  children,
}: {
  readonly testId: string;
  readonly title: string;
  readonly actionLabel?: string;
  readonly actionTo?: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section
      data-testid={testId}
      className="border-border bg-card shadow-admin-card rounded-lg border p-5"
    >
      <div className="flex items-center justify-between gap-4">
        <h2 className="font-display text-foreground text-start text-xl font-semibold">{title}</h2>
        {actionLabel && actionTo ? (
          <Link
            to={actionTo}
            className="text-foreground hover:text-admin-lime-hover shrink-0 text-sm font-medium"
          >
            {actionLabel}
          </Link>
        ) : null}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function renderRecentOrderRow(order: StoreOrderRow): React.JSX.Element {
  return (
    <li
      key={order.id}
      className="border-border flex items-center justify-between gap-4 border-t py-3 first:border-t-0 first:pt-0 last:pb-0"
    >
      <div className="min-w-0 text-start">
        <p className="text-foreground truncate text-sm font-medium">
          {order.customer_name_snapshot}
        </p>
        <p className="text-muted-foreground mt-1 text-xs" dir="ltr">
          #{order.id.slice(0, 8)}
        </p>
      </div>
      <span className="text-muted-foreground shrink-0 text-sm">{order.item_count}</span>
    </li>
  );
}

function StockAttentionRow({ row }: { readonly row: StockWorklistRow }): React.JSX.Element {
  const { t, lang } = useTranslation();
  return (
    <li className="border-border border-t py-3 first:border-t-0 first:pt-0 last:pb-0">
      <Link
        to="/stock"
        aria-label={t('admin.stock.worklist.viewStockLabel', {
          product: row.productName,
          variant: row.optionSummary || t('admin.stock.worklist.defaultVariant'),
          // React escapes this attribute; keep the merchant identity literal.
          interpolation: { escapeValue: false },
        })}
        className="focus-visible:ring-ring flex min-h-11 items-center justify-between gap-4 rounded-md focus-visible:outline-none focus-visible:ring-2"
      >
        <div className="min-w-0 text-start [overflow-wrap:anywhere]">
          <p dir="auto" className="text-foreground text-sm font-medium">
            {row.productName}
          </p>
          <p dir="auto" className="text-muted-foreground mt-1 text-xs">
            {row.optionSummary || t('admin.stock.worklist.defaultVariant')}
          </p>
          {!row.productPublished ? (
            <p className="text-muted-foreground mt-1 text-xs">
              {t('admin.stock.worklist.unpublished')}
            </p>
          ) : null}
        </div>
        <span
          className={cn(
            'shrink-0 text-lg font-semibold tabular-nums',
            row.status === 'out_of_stock'
              ? 'text-terracotta'
              : 'text-amber-700 dark:text-amber-200',
          )}
        >
          {new Intl.NumberFormat(lang).format(row.availableUnits)}
        </span>
      </Link>
    </li>
  );
}

export function DashboardRoute(): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const location = useLocation();
  const state = (location.state ?? {}) as DashboardLocationState;
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);

  // F-004 Phase 4d: dialog open state for publish/unpublish confirmations.
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);

  // Fetch store details for display. F-007 §4.4: the `['store', storeId]`
  // query + its M1 cache-shape contract lives in `useStore(storeId)`.
  const store = useStore(storeId);
  const products = useProducts(storeId);
  const incomingOrders = useStoreOrders({
    storeId,
    status: 'ordered',
    sortBy: 'created_at_desc',
    search: null,
  });
  const stock = useStockWorklist(supabase, {
    storeId,
    userId: authSession.user?.id ?? null,
    scope: 'active',
    filter: 'all',
    query: '',
    pageSize: 4,
  });

  const storeRow = store.data;
  const isPublished = storeRow?.published === true;
  const badgeStatus: 'draft' | 'published' = isPublished ? 'published' : 'draft';
  const publicUrl = storeRow ? buildStoreUrl(storeRow.slug) : '';
  const productRows = products.data ?? [];
  const publishedProducts = productRows.filter((product) => product.published).length;
  const recentIncomingOrders = incomingOrders.rows.slice(0, 3);
  const stockCounts = stock.page?.scopeCounts;
  const stockAttentionRows = stock.rows.filter((row) => row.status !== 'healthy').slice(0, 4);
  const stockUnavailable =
    stock.accessDenied || Boolean(stock.initialError) || (!stock.isLoading && !stock.page);
  const stockNumber = (value: number): string => new Intl.NumberFormat(lang).format(value);
  const incomingOrderMetricValue = `${incomingOrders.rows.length}${incomingOrders.hasMore ? '+' : ''}`;

  return (
    <main
      dir={dir}
      data-testid="dashboard-route"
      className="mx-auto w-full max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8"
    >
      <header
        data-testid="dashboard-header"
        className="border-border bg-card shadow-admin-card rounded-lg border p-5"
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 text-start">
            <p className="text-admin-lime-hover text-sm font-semibold">
              {t('admin.dashboard.title')}
            </p>
            <h1 className="font-display text-foreground mt-2 text-3xl font-semibold">
              {storeRow?.name ?? t('admin.dashboard.title')}
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl text-sm leading-relaxed">
              {t('admin.dashboard.subtitle')}
            </p>
          </div>
          {storeRow ? (
            <div className="flex items-center gap-3 lg:justify-end">
              {storeRow.logo_url ? (
                <img
                  src={storeRow.logo_url}
                  alt={storeRow.name}
                  className="border-border size-14 rounded-lg border object-cover"
                  data-testid="dashboard-store-logo"
                />
              ) : null}
              <StoreStatusBadge status={badgeStatus} />
            </div>
          ) : null}
        </div>

        {storeRow ? (
          <div className="border-border mt-5 border-t pt-5">
            <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
              <div className="min-w-0 text-start">
                <h2
                  className="text-foreground text-lg font-semibold"
                  data-testid="dashboard-store-name"
                >
                  {storeRow.name}
                </h2>
                {storeRow.description ? (
                  <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                    {storeRow.description}
                  </p>
                ) : null}
                <p
                  className="text-muted-foreground mt-2 truncate text-sm"
                  data-testid="dashboard-store-url"
                >
                  <span>{t('admin.dashboard.storeUrl')}: </span>
                  <span dir="ltr">{publicUrl}</span>
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </header>

      {state.justCreatedStore ? (
        <div
          role="status"
          data-testid="dashboard-store-created-banner"
          className="border-admin-lime/40 bg-admin-lime-light/40 text-foreground rounded-lg border p-4 text-start"
        >
          {t('admin.dashboard.storeCreatedBanner')}
        </div>
      ) : null}

      {state.logoFailed ? (
        <div
          role="alert"
          data-testid="dashboard-logo-failed-warning"
          className="border-destructive/30 bg-destructive/10 text-destructive rounded-lg border p-4 text-start"
        >
          {t('admin.dashboard.logoUploadWarning')}
        </div>
      ) : null}

      {storeRow ? (
        <>
          <section
            data-testid="dashboard-metrics"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
          >
            <DashboardMetricCard
              testId="dashboard-metric-products"
              title={t('admin.dashboard.metrics.products.title')}
              value={String(productRows.length)}
              helper={t('admin.dashboard.metrics.products.helper', {
                published: publishedProducts,
                count: productRows.length,
              })}
              icon={Package}
              to="/products"
              ariaLabel={t('admin.dashboard.metrics.products.ariaLabel')}
              isLoading={products.isLoading}
              isError={products.isError}
              loadingLabel={t('admin.dashboard.metrics.loadingValue')}
              errorLabel={t('admin.dashboard.metrics.errorValue')}
              loadingHelper={t('admin.dashboard.metrics.loadingHelper')}
              errorHelper={t('admin.dashboard.metrics.errorHelper')}
            />
            <DashboardMetricCard
              testId="dashboard-metric-orders"
              title={t('admin.dashboard.metrics.orders.title')}
              value={incomingOrderMetricValue}
              helper={t('admin.dashboard.metrics.orders.helper')}
              icon={ShoppingCart}
              to="/orders"
              ariaLabel={t('admin.dashboard.metrics.orders.ariaLabel')}
              isLoading={incomingOrders.isLoading}
              isError={incomingOrders.isError}
              loadingLabel={t('admin.dashboard.metrics.loadingValue')}
              errorLabel={t('admin.dashboard.metrics.errorValue')}
              loadingHelper={t('admin.dashboard.metrics.loadingHelper')}
              errorHelper={t('admin.dashboard.metrics.errorHelper')}
            />
            <DashboardMetricCard
              testId="dashboard-metric-stock"
              title={t('admin.dashboard.metrics.stock.title')}
              value={stockCounts ? stockNumber(stockCounts.lowStock + stockCounts.outOfStock) : '—'}
              helper={t('admin.dashboard.metrics.stock.helper', {
                low: stockCounts ? stockNumber(stockCounts.lowStock) : '—',
                out: stockCounts ? stockNumber(stockCounts.outOfStock) : '—',
              })}
              icon={Boxes}
              to="/stock"
              ariaLabel={t('admin.dashboard.metrics.stock.ariaLabel')}
              isLoading={stock.isLoading}
              isError={stockUnavailable}
              loadingLabel={t('admin.dashboard.metrics.loadingValue')}
              errorLabel={t('admin.dashboard.metrics.errorValue')}
              loadingHelper={t('admin.dashboard.metrics.loadingHelper')}
              errorHelper={t('admin.dashboard.metrics.errorHelper')}
            />
            <DashboardMetricCard
              testId="dashboard-metric-storefront"
              title={t('admin.dashboard.metrics.storefront.title')}
              value={t(
                isPublished
                  ? 'admin.dashboard.metrics.storefront.published'
                  : 'admin.dashboard.metrics.storefront.draft',
              )}
              helper={t('admin.dashboard.metrics.storefront.helper')}
              icon={Store}
              ariaLabel={t('admin.dashboard.metrics.storefront.ariaLabel')}
              loadingLabel={t('admin.dashboard.metrics.loadingValue')}
              errorLabel={t('admin.dashboard.metrics.errorValue')}
              loadingHelper={t('admin.dashboard.metrics.loadingHelper')}
              errorHelper={t('admin.dashboard.metrics.errorHelper')}
            />
          </section>

          <p className="text-muted-foreground text-start text-sm">
            {t('admin.dashboard.snapshotNotice')}
          </p>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
            <div className="space-y-6">
              <DashboardPanel
                testId="dashboard-recent-orders-panel"
                title={t('admin.dashboard.recentOrders.title')}
                actionLabel={t('admin.dashboard.recentOrders.viewAll')}
                actionTo="/orders"
              >
                {incomingOrders.isLoading ? (
                  <p className="text-muted-foreground text-start text-sm">
                    {t('admin.dashboard.recentOrders.loading')}
                  </p>
                ) : incomingOrders.isError ? (
                  <p className="text-terracotta text-start text-sm">
                    {t('admin.dashboard.recentOrders.error')}
                  </p>
                ) : recentIncomingOrders.length > 0 ? (
                  <ul>{recentIncomingOrders.map(renderRecentOrderRow)}</ul>
                ) : (
                  <p className="text-muted-foreground text-start text-sm">
                    {t('admin.dashboard.recentOrders.empty')}
                  </p>
                )}
              </DashboardPanel>

              <DashboardPanel
                testId="dashboard-stock-attention-panel"
                title={t('admin.dashboard.stockAttention.title')}
                actionLabel={t('admin.dashboard.stockAttention.viewStock')}
                actionTo="/stock"
              >
                {stock.isLoading ? (
                  <p className="text-muted-foreground text-start text-sm">
                    {t('admin.dashboard.stockAttention.loading')}
                  </p>
                ) : stockUnavailable ? (
                  <p className="text-terracotta text-start text-sm">
                    {t('admin.dashboard.stockAttention.error')}
                  </p>
                ) : stockAttentionRows.length > 0 ? (
                  <ul>
                    {stockAttentionRows.map((row) => (
                      <StockAttentionRow key={row.variantId} row={row} />
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground text-start text-sm">
                    {t('admin.dashboard.stockAttention.empty')}
                  </p>
                )}
                {stock.page ? (
                  <p className="text-muted-foreground mt-3 text-xs">
                    <time dateTime={stock.page.observedAt}>
                      {t('admin.stock.worklist.lastChecked', {
                        time: new Intl.DateTimeFormat(lang, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        }).format(new Date(stock.page.observedAt)),
                        // Intl output is rendered as React text, which supplies escaping.
                        interpolation: { escapeValue: false },
                      })}
                    </time>
                  </p>
                ) : null}
                {stock.refreshError ? (
                  <p role="alert" className="text-destructive mt-2 text-xs">
                    {t('admin.stock.worklist.refreshFailed')}
                  </p>
                ) : null}
              </DashboardPanel>
            </div>

            <aside className="space-y-6">
              {isPublished ? (
                <PublishedStoreCard
                  storeSlug={storeRow.slug}
                  onUnpublishClick={() => setUnpublishOpen(true)}
                />
              ) : (
                <PublishStoreCard
                  storeSlug={storeRow.slug}
                  onPublishClick={() => setPublishOpen(true)}
                />
              )}

              <section
                data-testid="dashboard-next-step-card"
                className="border-border bg-card shadow-admin-card rounded-lg border p-5"
              >
                <div className="flex items-start gap-3 text-start">
                  <span className="bg-admin-lime-light text-foreground inline-flex size-10 shrink-0 items-center justify-center rounded-md">
                    <AlertTriangle aria-hidden={true} className="size-5" />
                  </span>
                  <div>
                    <h2 className="font-display text-foreground text-xl font-semibold">
                      {t('admin.dashboard.nextStepCard.title')}
                    </h2>
                    <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                      {t('admin.dashboard.nextStepCard.description')}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Link
                    to="/products/new"
                    className="bg-admin-lime text-admin-lime-foreground hover:bg-admin-lime-hover inline-flex min-h-11 items-center gap-2 rounded-md px-4 text-sm font-semibold"
                    data-testid="dashboard-add-product"
                  >
                    <Plus aria-hidden={true} className="size-4" />
                    {t('admin.dashboard.addProduct')}
                  </Link>
                  <Link
                    to="/products"
                    className="border-border text-foreground hover:bg-admin-surface-muted inline-flex min-h-11 items-center rounded-md border px-4 text-sm font-semibold"
                    data-testid="dashboard-view-products"
                  >
                    {t('admin.dashboard.viewProducts')}
                  </Link>
                </div>
              </section>
            </aside>
          </div>

          <PublishStoreDialog
            storeId={storeRow.id}
            storeSlug={storeRow.slug}
            open={publishOpen}
            onOpenChange={setPublishOpen}
          />
          <UnpublishStoreDialog
            storeId={storeRow.id}
            open={unpublishOpen}
            onOpenChange={setUnpublishOpen}
          />
        </>
      ) : store.isLoading ? (
        <p className="text-muted-foreground mt-6 text-start">{t('common.loading')}</p>
      ) : null}
    </main>
  );
}
